/**
 * Gender Party Viewer Page
 * Mobile-optimized page for users to vote.
 * When user votes → their emoji bursts up from the button.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation } from "urql";
import {
  useGenderPartySocket,
  type GenderVote,
  type GenderPartyVoteCounts,
} from "./hooks";
import { PhotoCarousel, VotingBar, VoteButtons, RevealAnimation } from "./components";
import { VoteBurst } from "./components/VoteBurst";
import { useAuth } from "@/lib/auth";
import { safeShowAlert, haptic } from "@/lib/telegram";
import { getErrorMessage } from "@/lib/graphql";
import styles from "./GenderPartyViewerPage.module.css";

const VOTE_MUTATION = `
  mutation Vote($genderPartyId: ID!, $vote: GenderVote!) {
    vote(genderPartyId: $genderPartyId, vote: $vote) {
      id
      status
    }
  }
`;

export function GenderPartyViewerPage() {
  const { id } = useParams<{ id: string }>();
  const { account, isAuthenticated } = useAuth();
  const [voteMutationResult, executeVote] = useMutation(VOTE_MUTATION);

  const [showRevealAnimation, setShowRevealAnimation] = useState(false);
  const [revealedAnswer, setRevealedAnswer] = useState<GenderVote | null>(null);

  // Vote burst: trigger when user submits their own vote
  const [burstTrigger, setBurstTrigger] = useState(0);
  const [myBurstBoyDelta, setMyBurstBoyDelta] = useState(0);
  const [myBurstGirlDelta, setMyBurstGirlDelta] = useState(0);

  // Optimistic vote — immediately locks buttons after voting
  const [optimisticVote, setOptimisticVote] = useState<GenderVote | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute("data-gender-party-page", "true");
    return () => document.documentElement.removeAttribute("data-gender-party-page");
  }, []);

  const handleAnswer = useCallback((answer: GenderVote, _voteCounts: GenderPartyVoteCounts) => {
    setRevealedAnswer(answer);
    setShowRevealAnimation(true);
  }, []);

  const {
    isConnected,
    isJoined,
    error,
    genderParty,
    photos,
    voteCounts,
    myVote,
    isCompleted,
  } = useGenderPartySocket({
    genderPartyId: id ?? "",
    accountId: account?.id,
    onAnswer: handleAnswer,
  });

  const handleVote = useCallback(
    async (vote: GenderVote) => {
      if (!id) return;

      // Optimistically set vote immediately to lock buttons
      setOptimisticVote(vote);

      // Trigger personal burst before awaiting mutation
      if (vote === "BOY") {
        setMyBurstBoyDelta(3);
        setMyBurstGirlDelta(0);
      } else {
        setMyBurstBoyDelta(0);
        setMyBurstGirlDelta(3);
      }
      setBurstTrigger((t) => t + 1);

      try {
        const result = await executeVote({ genderPartyId: id, vote });
        if (result.error) {
          haptic("error");
          safeShowAlert(getErrorMessage(result.error, "Не удалось проголосовать"));
          // Revert optimistic vote on error
          setOptimisticVote(null);
        }
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Не удалось проголосовать"));
        // Revert optimistic vote on error
        setOptimisticVote(null);
      }
    },
    [id, executeVote]
  );

  if (!id) {
    return (
      <div className={styles.page}>
        <div className={styles.errorContainer}>
          <h2>Голосование не найдено</h2>
          <Link to="/" className={styles.homeLink}>На главную</Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <div className={styles.authContainer}>
          <div className={styles.authIcon}>🗳️</div>
          <h2>Войдите, чтобы проголосовать</h2>
          <p>Для участия в голосовании необходимо авторизоваться</p>
          <Link to="/login" className={styles.loginButton}>Войти</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorContainer}>
          <h2>Ошибка</h2>
          <p>{error}</p>
          <button className={styles.retryButton} onClick={() => window.location.reload()}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  if (!isConnected || !isJoined || !genderParty) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (showRevealAnimation && revealedAnswer) {
    return (
      <RevealAnimation
        answer={revealedAnswer}
        onComplete={() => setShowRevealAnimation(false)}
      />
    );
  }

  // Use optimistic vote if set, otherwise use server vote
  const effectiveVote = optimisticVote ?? myVote;

  const canVote = genderParty.status === "VOTING_OPEN";
  const showResults =
    genderParty.showResultsMode === "always" ||
    (genderParty.showResultsMode === "after_vote" && effectiveVote !== null) ||
    genderParty.status === "REVEALED" ||
    genderParty.status === "COMPLETED";

  const boyPct = voteCounts?.boyPercentage ?? 50;
  const girlPct = voteCounts?.girlPercentage ?? 50;
  const boyOrbOpacity = 0.12 + (boyPct / 100) * 0.28;
  const girlOrbOpacity = 0.12 + (girlPct / 100) * 0.28;

  return (
    <div
      className={styles.page}
      style={{
        "--boy-orb-opacity": boyOrbOpacity,
        "--girl-orb-opacity": girlOrbOpacity,
      } as React.CSSProperties}
    >
      {/* Subtle background orbs */}
      <div className={styles.bgOrbs} aria-hidden="true">
        <div className={styles.boyOrb} />
        <div className={styles.girlOrb} />
      </div>

      {/* Vote burst when user votes */}
      <VoteBurst
        boyDelta={myBurstBoyDelta}
        girlDelta={myBurstGirlDelta}
        trigger={burstTrigger}
        size="medium"
      />

      <header className={styles.header}>
        <h1 className={styles.title}>{genderParty.name}</h1>
        <div className={styles.coupleInfo}>
          <span className={`${styles.partnerName} ${styles.partnerBoy}`}>
            {genderParty.partnerOneName}
          </span>
          <span className={styles.and}>&</span>
          <span className={`${styles.partnerName} ${styles.partnerGirl}`}>
            {genderParty.partnerTwoName}
          </span>
        </div>
        {genderParty.isTest && <span className={styles.testBadge}>ТЕСТ</span>}
      </header>

      <main className={styles.main}>
        <PhotoCarousel
          photos={photos}
          couplePhotoUrl={genderParty.couplePhotoUrl}
          autoPlay={!canVote}
          className={styles.carousel}
        />

        {genderParty.description && (
          <p className={styles.description}>{genderParty.description}</p>
        )}

        <VotingBar
          voteCounts={voteCounts}
          myVote={effectiveVote}
          showResults={showResults}
          revealedAnswer={isCompleted ? revealedAnswer ?? genderParty.answer : null}
          className={styles.votingBar}
        />

        {canVote && (
          <VoteButtons
            myVote={effectiveVote}
            onVote={handleVote}
            allowVoteChange={genderParty.allowVoteChange}
            disabled={voteMutationResult.fetching}
          />
        )}

        {!canVote && effectiveVote && (
          <div className={styles.votedMessage}>
            <span className={styles.votedEmoji}>
              {effectiveVote === "BOY" ? "👦" : "👧"}
            </span>
            <span>Вы проголосовали за {effectiveVote === "BOY" ? "мальчика" : "девочку"}</span>
          </div>
        )}

        {genderParty.status === "DRAFT" && (
          <div className={styles.statusMessage}>
            <span className={styles.statusIcon}>⏳</span>
            <span>Голосование скоро начнётся</span>
          </div>
        )}

        {genderParty.status === "VOTING_CLOSED" && !effectiveVote && (
          <div className={styles.statusMessage}>
            <span className={styles.statusIcon}>🔒</span>
            <span>Голосование закрыто</span>
          </div>
        )}
      </main>
    </div>
  );
}
